// ApiError captures the {error:{code,message}} envelope every console-api
// handler emits. Carrying status separately lets UI code branch on it
// (e.g. 402 → upsell modal) without re-parsing the body.
export class ApiError extends Error {
  status: number
  code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

// apiErrorMessage returns the human-readable message of an ApiError, or a
// caller-supplied fallback for anything else (a thrown non-ApiError, a
// bare string, etc.). Centralises the `e instanceof ApiError ? e.message
// : '…'` pattern that recurs across the page components (FE-4).
export function apiErrorMessage(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback
}

// parseApiError builds an ApiError from a non-OK Response by reading the
// shared {error:{code,message}} envelope, falling back to a generic
// http_error code + a status-derived message. Shared by apiFetch and the
// multipart upload path in useVersions so the two parse the same envelope
// the same way (FE-5). The RFC-6749 token-error shape in oauth.ts is
// deliberately NOT routed through here — it carries {error, error_description}
// at the top level, not the nested console-api envelope.
export async function parseApiError(res: Response): Promise<ApiError> {
  let parsed: unknown = null
  const ct = res.headers.get('Content-Type') ?? ''
  if (ct.includes('application/json')) {
    try {
      parsed = await res.json()
    } catch {
      parsed = null
    }
  }
  const code = extractCode(parsed) ?? 'http_error'
  const message = extractMessage(parsed) ?? `request failed (${res.status})`
  return new ApiError(res.status, code, message)
}

// IdTokenProvider returns the current Firebase ID token, refreshed if
// necessary. Wired up in api/index.ts via the AuthContext so individual
// resource modules don't have to touch Firebase directly.
export type IdTokenProvider = () => Promise<string | null>

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  // Override the default JSON content-type if needed (e.g. multipart).
  headers?: Record<string, string>
  signal?: AbortSignal
}

// Every request gets a deadline. Without one, a stalled connection (half-open
// socket, wedged backend stream) leaves the fetch promise pending forever —
// and react-query only retries on REJECTION, so a single hung request would
// pin loading UI (e.g. the Billing spinner) until a manual reload. 30s sits
// far above any healthy endpoint (the slowest are the Stripe-coupled billing
// calls, a few seconds) while still converting a wedge into a retryable
// failure.
const REQUEST_TIMEOUT_MS = 30_000

// composeSignal combines the caller's abort signal (react-query cancellation,
// component unmount) with the request deadline — whichever fires first aborts
// the fetch. Composed manually rather than via AbortSignal.any() for broader
// browser support.
function composeSignal(caller: AbortSignal | undefined): AbortSignal {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS)
  if (!caller) return timeout
  const ctrl = new AbortController()
  const forward = (s: AbortSignal) => {
    if (s.aborted) ctrl.abort(s.reason)
    else s.addEventListener('abort', () => ctrl.abort(s.reason), { once: true })
  }
  forward(caller)
  forward(timeout)
  return ctrl.signal
}

// apiFetch is the authed network entrypoint. Attaches Authorization,
// serialises the body to JSON, parses the {error:{code,message}}
// envelope on non-2xx, and returns the JSON body typed as T.
//
// A successful 204 returns `undefined as T` because there's nothing to
// parse. Callers that expect 204 should type the function as
// `Promise<void>`.
export async function apiFetch<T>(
  path: string,
  getIdToken: IdTokenProvider,
  opts: RequestOptions = {},
): Promise<T> {
  const token = await getIdToken()
  if (!token) {
    throw new ApiError(401, 'unauthenticated', 'not signed in')
  }
  return sendRequest<T>(path, { Authorization: `Bearer ${token}`, ...opts.headers }, opts)
}

// publicFetch is apiFetch for the few console-api routes a signed-out visitor
// may call (the forgot-password pair). It sends no Authorization header and
// otherwise behaves identically — same deadline, same error envelope.
export async function publicFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return sendRequest<T>(path, { ...opts.headers }, opts)
}

async function sendRequest<T>(
  path: string,
  headers: Record<string, string>,
  opts: RequestOptions,
): Promise<T> {
  let body: BodyInit | undefined
  if (opts.body !== undefined) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
    body = JSON.stringify(opts.body)
  }

  // exactOptionalPropertyTypes: don't pass undefined for body — build the
  // init object incrementally so RequestInit's `body: ... | null` constraint
  // is satisfied. The signal is always set: caller signal + deadline.
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    signal: composeSignal(opts.signal),
  }
  if (body !== undefined) init.body = body

  let res: Response
  try {
    res = await fetch(path, init)
  } catch (e) {
    // Network failure (offline, CORS, abort, deadline). Surface as a
    // synthetic ApiError with status 0 so callers can branch on the same
    // shape; react-query treats the rejection as retryable.
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      throw new ApiError(0, 'network_error', `request timed out (${path})`)
    }
    throw new ApiError(0, 'network_error', toErrorMessage(e))
  }

  if (res.status === 204) {
    return undefined as T
  }

  if (!res.ok) {
    throw await parseApiError(res)
  }

  let parsed: unknown = null
  const ct = res.headers.get('Content-Type') ?? ''
  if (ct.includes('application/json')) {
    try {
      parsed = await res.json()
    } catch {
      parsed = null
    }
  }
  return parsed as T
}

function extractCode(parsed: unknown): string | null {
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    'error' in parsed &&
    typeof (parsed as { error: unknown }).error === 'object' &&
    (parsed as { error: { code?: unknown } }).error !== null
  ) {
    const code = (parsed as { error: { code?: unknown } }).error.code
    return typeof code === 'string' ? code : null
  }
  return null
}

function extractMessage(parsed: unknown): string | null {
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    'error' in parsed &&
    typeof (parsed as { error: unknown }).error === 'object' &&
    (parsed as { error: { message?: unknown } }).error !== null
  ) {
    const m = (parsed as { error: { message?: unknown } }).error.message
    return typeof m === 'string' ? m : null
  }
  return null
}

function toErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message
  return String(e)
}
